/* Bugünkü ana sayfa, blok belgesi olarak.

   Panelde hiç düzenleme yapılmamışken üretici bu belgeyi basıyor ve sonuç
   elle yazılmış sayfanın aynısı (golden test). Editör de ilk açılışta bunu
   gösteriyor; yönetici bir şeyi değiştirip kaydettiği an belge
   `site_design`'a yazılıyor ve bundan sonra kaynak o.

   Metinler 2026-10-17'deki bölüm şablonlarından birebir alındı. Anahtarlar
   (`key`) bugünkü i18n anahtarları: i18n.js'teki İngilizce karşılıklar
   hiçbir şey taşınmadan çalışmaya devam ediyor.

   Hizmetler ve seçili işler veritabanından geliyor (`content`) — bu belge
   onların başlangıç hâli. Kaydedildikten sonra ana sayfadaki kartlar
   belgede yaşıyor. */

import type { Block, Card, Rich } from '@frawgraphy/shared';
import type { SiteContent } from '../content.ts';

const t = (tr: string): Rich => ({ tr });

/** Veritabanının `{tr, en}` metnini temizlenmiş düz metne çevirir (yalnızca TR). */
function escaped(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function head(id: string, index: string, title: string, lede?: string): Block[] {
  const blocks: Block[] = [
    { id: `${id}-index`, key: `${id}.index`, type: 'eyebrow', text: t(index) },
    { id: `${id}-title`, key: `${id}.title`, type: 'heading', level: 2, text: t(title) }
  ];
  if (lede) blocks.push({ id: `${id}-lede`, key: `${id}.lede`, type: 'paragraph', variant: 'lede', text: t(lede) });
  return blocks;
}

export function defaultHome(content: SiteContent): Block[] {
  const services: Card[] = content.services.map((s, i) => ({
    id: `service-${i + 1}`,
    key: `services.${i + 1}`,
    title: t(escaped(s.title.tr)),
    desc: t(escaped(s.desc.tr)),
    meta: t(escaped(s.meta.tr)),
    ...(s.title.en !== undefined && s.title.en === s.title.tr ? { titleLang: 'en' as const } : {})
  }));

  return [
    {
      id: 'hero',
      key: 'hero',
      type: 'hero',
      eyebrow: t('Est. 2016 — Independent'),
      lines: [
        { id: 'hero-line-1', text: 'Fashion' },
        { id: 'hero-line-2', text: 'Image', indent: true, amp: true },
        { id: 'hero-line-3', text: 'Motion' }
      ],
      lede: t('Editorial, kampanya ve lookbook çekimleri üreten bağımsız bir moda fotoğraf stüdyosu. Işığı bir malzeme, kadrajı bir cümle gibi kullanıyoruz.'),
      buttons: [
        { id: 'hero-cta-1', key: 'hero.cta1', label: t('Seçili işler'), href: '#work', variant: 'primary', arrow: true },
        { id: 'hero-cta-2', key: 'hero.cta2', label: t('Çekim planla'), href: '#contact', variant: 'ghost' }
      ],
      hint: t('İmlecinizi gezdirin — renk objektifi takip eder')
    },
    {
      id: 'work',
      type: 'section',
      anchor: 'work',
      alt: false,
      head: head('work', 'İşler', 'Seçili <em>işler</em>', 'Marka kampanyalarından bağımsız editoryal projelere. Her seri, tek bir ışık kararı etrafında kurulur.'),
      blocks: [
        {
          id: 'work-coverflow',
          type: 'coverflow',
          photos: content.featured.map((p) => p.id),
          more: { id: 'work-more', key: 'work.browseAll', label: t('Tüm işleri gör'), href: 'works.html', variant: 'ghost', arrow: true }
        }
      ]
    },
    {
      id: 'video',
      type: 'section',
      anchor: 'video',
      alt: true,
      head: head('video', 'Videografi', 'Hareketli <em>kayıt</em>', 'Fotoğrafla aynı sette çekilen kısa format videolar. Aynı ışık şeması, aynı ekip, tek günde iki çıktı.'),
      blocks: [
        {
          id: 'video-reels',
          type: 'reels',
          items: [
            {
              id: 'reel-1',
              key: 'reel.1',
              title: t('Sessiz Akış'),
              spec: t('16:9 &amp; 9:16 · RENK DÜZENLEMESİ · SES TASARIMI'),
              note: t('Atelier Sur FW25 kampanyası. Fotoğraf çekimiyle aynı sette, aynı ışık şemasıyla kaydedildi; basılı ve sosyal formatlar tek günde teslim edildi.'),
              time: '01:24',
              poster: 'https://picsum.photos/id/1011/1280/720?grayscale',
              video: 'assets/video/sessiz-akis.mp4',
              focus: '51% 55%',
              label: t('Su üzerinde çekilmiş kampanya filmi')
            },
            {
              id: 'reel-2',
              key: 'reel.2',
              title: t('Cadde Kayıtları'),
              spec: t('9:16 DİKEY · SOKAK IŞIĞI · HIZLI KURGU'),
              note: t('Koleksiyonun tamamı için dikey format lookbook filmi. Doğal sokak ışığında, sabit kadraj sistemiyle bir günde çekildi.'),
              time: '00:38',
              poster: 'https://picsum.photos/id/342/1280/720?grayscale',
              video: 'assets/video/cadde-kayitlari.mp4',
              focus: '52% 50%',
              label: t('Sokakta çekilmiş lookbook filmi')
            },
            {
              id: 'reel-3',
              key: 'reel.3',
              title: t('Ufuk Çizgisi'),
              spec: t('STOP-MOTION · CANLI ÇEKİM · KOMPOZİT'),
              note: t('Marka filmi. Kare kare çekilen stop-motion bölümler canlı çekimle birleştirildi; geçişler sette planlandı, kurguda değil.'),
              time: '02:05',
              poster: 'https://picsum.photos/id/349/1280/720?grayscale',
              video: 'assets/video/ufuk-cizgisi.mp4',
              focus: '81% 44%',
              label: t('Şehir siluetiyle çekilmiş marka filmi')
            },
            {
              id: 'reel-4',
              key: 'reel.4',
              title: t('Set Günlüğü'),
              spec: t('BACKSTAGE · UZUN FORMAT · RÖPORTAJ'),
              note: t('Üç günlük bir kampanya çekiminin belgeseli. Ekip, prova ve karar anları; müşteriye süreci anlatmak için kaydedildi.'),
              time: '03:12',
              poster: 'https://picsum.photos/id/373/1280/720?grayscale',
              video: 'assets/video/set-gunlugu.mp4',
              focus: '34% 47%',
              label: t('Backstage belgeseli')
            }
          ]
        }
      ]
    },
    {
      id: 'digital',
      type: 'section',
      anchor: 'digital',
      alt: false,
      head: [
        ...head('digital', 'Dijital Üretim', 'Üretken <em>görsel</em>', 'Yapay zekâ ile ürettiğimiz kavram görselleri. Kampanya öncesi yön bulmak, imkânsız mekânları denemek ve moodboard kurmak için kullanıyoruz.'),
        {
          id: 'digital-disclosure',
          key: 'digital.disclosure',
          type: 'note',
          text: t('Bu bölümdeki görsellerin tamamı yapay zekâ ile üretilmiştir; kamerayla çekilmiş bir kaydı temsil etmez.')
        }
      ],
      blocks: [
        {
          id: 'digital-cards',
          key: 'digital',
          type: 'cards',
          variant: 'ai',
          items: [
            aiCard(1, 'Yapay zekâ ile üretilmiş kürk doku çalışması', 1205, '2026', 'Sentetik Kumaş', 'Doku araştırması — 32 varyasyon'),
            aiCard(2, 'Yapay zekâ ile üretilmiş su yüzeyi çalışması', 1125, '2026', 'Yüzey / 02', 'Kampanya ön görselleştirme'),
            aiCard(3, 'Yapay zekâ ile üretilmiş yeşil tonlu doku çalışması', 1205, '2025', 'Işık Tohumu', 'Set tasarımı denemesi')
          ]
        }
      ]
    },
    {
      id: 'studio',
      type: 'section',
      anchor: 'studio',
      alt: true,
      head: head('studio', 'Stüdyo', 'Işık, sabır ve <em>bir kadraj</em>'),
      blocks: [
        {
          id: 'studio-split',
          type: 'split',
          media: { src: 'assets/img/studio-1100.jpg', alt: t('Stüdyo ışığı altında çekilmiş portre çalışması'), width: 1100, height: 1374 },
          caption: t('320 m² doğal ışıklı stüdyo — Beyoğlu'),
          captionKey: 'studio.caption',
          blocks: [
            {
              id: 'studio-p1',
              key: 'studio.p1',
              type: 'paragraph',
              variant: 'lead',
              reveal: true,
              text: t("2016'dan bu yana moda markaları, dergiler ve stil ekipleriyle çalışıyoruz. Ekip küçük, kararlar hızlı, prova sayısı fazla.")
            },
            {
              id: 'studio-p2',
              key: 'studio.p2',
              type: 'paragraph',
              reveal: true,
              text: t('Çekim öncesi referans setini birlikte kuruyoruz; sette ışık şemasını tek bir fikir üzerine oturtuyoruz. Teslim, renk düzenlemesi ve rötuş dahil ortalama on iş günü içinde tamamlanır.')
            },
            {
              id: 'studio-stats',
              type: 'facts',
              variant: 'stats',
              items: [
                { id: 'stat-1', key: 'studio.stat1', label: t('Yıl'), value: t('09') },
                { id: 'stat-2', key: 'studio.stat2', label: t('Yayın'), value: t('140') },
                { id: 'stat-3', key: 'studio.stat3', label: t('Marka'), value: t('62') },
                { id: 'stat-4', key: 'studio.stat4', label: t('Ülke'), value: t('11') }
              ]
            },
            {
              id: 'studio-cta',
              key: 'studio.ctaText',
              type: 'cta',
              text: t('Süreci merak ediyorsanız yazdıklarımıza göz atın.'),
              button: { id: 'studio-cta-btn', key: 'studio.ctaBtn', label: t('Nasıl yapıyoruz'), href: 'blog.html', variant: 'primary', arrow: true }
            }
          ]
        }
      ]
    },
    {
      id: 'services',
      type: 'section',
      anchor: 'services',
      alt: false,
      head: head('services', 'Hizmetler', 'Ne <em>yapıyoruz</em>'),
      blocks: [{ id: 'services-cards', type: 'cards', variant: 'service', items: services }]
    },
    {
      id: 'contact',
      type: 'section',
      anchor: 'contact',
      alt: true,
      head: head('contact', 'İletişim', 'Bir proje <em>anlatın</em>', 'Takvimimiz iki ay önceden dolar. Tarih ve kapsamı yazmanız yeterli.'),
      blocks: [
        {
          id: 'contact-form',
          key: 'contact',
          type: 'contact',
          info: [
            { id: 'info-studio', key: 'contact.studio', label: t('Stüdyo'), value: t('Meşrutiyet Caddesi 41<br>Beyoğlu, İstanbul') },
            { id: 'info-email', key: 'contact.emailLabel', label: t('E-posta'), value: t('<a href="mailto:studio@frawgraphy.com">studio@frawgraphy.com</a>') },
            { id: 'info-phone', key: 'contact.phone', label: t('Telefon'), value: t('<a href="tel:+905533245031">+90 553 324 50 31</a>') },
            { id: 'info-rep', key: 'contact.rep', valueKey: 'contact.repVal', label: t('Temsil'), value: t('Avrupa — Atelier Mgmt.<br>Türkiye — doğrudan') }
          ],
          note: t('Yanıt süresi: iki iş günü.')
        }
      ]
    }
  ];
}

function aiCard(n: number, alt: string, height: number, year: string, title: string, desc: string): Card {
  return {
    id: `digital-${n}`,
    key: `digital.${n}`,
    media: { src: `assets/img/ai-${n}-900.jpg`, alt: t(alt), width: 900, height },
    full: `assets/img/ai-${n}-full.jpg`,
    category: t('Kavram'),
    year,
    title: t(title),
    desc: t(desc)
  };
}
